'use client';

import { useField, useFormikContext } from 'formik';
import { RadioField, Address, EditableSection } from '@petsmart-ui/sparky';
import type { AccountAddressesSuccessResponse } from '@libs/common-next';

import type { BillingAddressSelectorProps } from './types';

import './BillingAddressSelector.scss';
import type {
    AddPaymentFormInterface,
    EditPaymentFormInterface,
} from '../PaymentForm/schema';

export function BillingAddressSelector({
    billingAddresses,
}: BillingAddressSelectorProps) {
    const [{ value: selectedAddressValue }] = useField('selectedAddressId');
    const { setFieldValue } = useFormikContext<
        AddPaymentFormInterface | EditPaymentFormInterface
    >();

    if (!billingAddresses?.length) {
        return null;
    }

    const onSelectChange = (value: string) => {
        setFieldValue('selectedAddressId', value);
    };

    const renderBillingAddressItem = (
        address: AccountAddressesSuccessResponse
    ) => (
        <EditableSection
            behavior="static"
            titleAfter={
                <Address
                    addressName={`${address.firstName} ${address.lastName}`}
                    extendedAddress={address.streetLine2}
                    street={address.streetLine1}
                    city={address.city}
                    state={address.stateProvinceAbbreviation.toUpperCase()}
                    postalCode={address.zipPostalCode}
                    showAddressName
                />
            }
        />
    );

    return (
        <div
            className="billing-address-selector"
            data-testid="billing-address-selector"
        >
            <RadioField
                selectedValue={selectedAddressValue}
                required
                onChange={onSelectChange}
            >
                {billingAddresses.map(address => (
                    <RadioField.Item
                        data-cs-mask
                        itemBefore={renderBillingAddressItem(address)}
                        text=""
                        value={address.addressId.toString()}
                        key={address.addressId.toString()}
                    />
                ))}
            </RadioField>
        </div>
    );
}
